import React, { useState, useMemo } from 'react';
import { User } from '../../types';
import FormField from './FormField';
import { ProfileData } from '../../services/profileService';
import { useAuth } from '../../AuthContext';

interface ProfileSectionProps {
  user: User;
  onProfileUpdate: (data: Partial<ProfileData>) => Promise<void>;
}



const ProfileSection: React.FC<ProfileSectionProps> = ({ user, onProfileUpdate }) => {
  const { currentUser } = useAuth();

  const authProvider = useMemo(() => {
    if (!currentUser) return 'unknown';
    const providers = currentUser.providerData?.map(p => p.providerId) || [];
    if (providers.includes('google.com')) return 'google';
    if (providers.includes('password')) return 'email';
    return 'unknown';
  }, [currentUser]);

  const [formData, setFormData] = useState({
    displayName: user.name,
    email: user.email,
    photoURL: user.avatar,
  });

  const [errors, setErrors] = useState<Partial<Record<'displayName' | 'email', string>>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Validate display name
    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Jméno je povinné';
    } else if (formData.displayName.trim().length < 2) {
      newErrors.displayName = 'Jméno musí mít alespoň 2 znaky';
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email je povinný';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Neplatný formát emailu';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setSuccessMessage('');

    try {
      await onProfileUpdate(formData);
      setSuccessMessage('Profil byl úspěšně aktualizován');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors({ displayName: 'Nepodařilo se aktualizovat profil. Zkuste to znovu.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // For now, we'll just create a preview URL
      // In a real app, you'd upload to Firebase Storage
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setFormData(prev => ({
            ...prev,
            photoURL: event.target!.result as string
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 rounded-2xl">
          <p className="text-sm font-medium text-green-600 dark:text-green-400">{successMessage}</p>
        </div>
      )}

      {/* Profile Picture */}
      <div className="flex items-center space-x-6">
        <div className="relative">
          <img
            className="w-20 h-20 rounded-full object-cover ring-4 ring-primary/30 dark:ring-primary/40"
            src={formData.photoURL}
            alt="Profile"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(formData.displayName)}&background=3b82f6&color=fff`;
            }}
          />
          <label
            htmlFor="profile-photo"
            className="absolute inset-0 w-20 h-20 rounded-full bg-primary/70 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
          >
            <span className="text-white text-sm">Změnit</span>
          </label>
          <input
            id="profile-photo"
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="sr-only"
          />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Profilová fotka</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Klikněte na obrázek pro změnu fotky
          </p>
        </div>
      </div>

      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Jméno"
          id="displayName"
          value={formData.displayName}
          onChange={(value) => setFormData(prev => ({ ...prev, displayName: value }))}
          error={errors.displayName}
          required
          placeholder="Vaše jméno"
        />

        <FormField
          label="Email"
          id="email"
          type="email"
          value={formData.email}
          onChange={(value) => setFormData(prev => ({ ...prev, email: value }))}
          error={errors.email}
          required
          placeholder="vas@email.cz"
          helpText={authProvider === 'google' ? 'Email nelze změnit u Google účtů' : 'Email je spravován přes Firebase'}
          disabled={true}
        />
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isLoading}
          className={`
            px-8 py-3 rounded-2xl text-sm font-bold tracking-wide transition-all duration-150
            focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800
            ${isLoading
              ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              : 'bg-primary hover:bg-primary/90 text-primary-foreground border-b-4 border-b-amber-600 active:border-b-0 active:translate-y-1'
            }
          `}
        >
          {isLoading ? 'Ukládám...' : 'Uložit změny'}
        </button>
      </div>
    </form>
  );
};

export default ProfileSection;